import { useGSAP } from '@gsap/react';
import { graphql } from 'gatsby';
import gsap from 'gsap';
import React, { useRef } from 'react';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import * as serializersStyles from '../../../serializers.module.scss';
import { BREAKPOINTS } from '../../../styles/breakpoints';
import { BackgroundColorTheme, ModuleBgColor } from '../../../templates/Page';
import { RawPortableText } from '../../../types/types';
import { clsx } from '../../../utils/utils';
import { CommonModuleProps } from '../../ModulesContent';
import GenericPortableText from '../GenericPortableText';
import Image, { BaseImageProps, ImageDimensions } from '../Image';
import ModuleLayout from '../ModuleLayout';
import * as styles from './TextWithBackgroundImageModule.module.scss';

export const TextWithBackgroundImageModuleFragment = graphql`
  fragment TextWithBackgroundImageModule on SanityPageTextWithBackgroundImageModule {
    title {
      ...LocaleStringFragment
    }
    text {
      _type
      _rawPt(resolveReferences: { maxDepth: 4 })
      _rawEn(resolveReferences: { maxDepth: 4 })
    }
    numbersTitle
    numbers {
      superTitle
      number
      numberUnit
      subtitle {
        ...LocaleStringFragment
      }
    }
    mobileImage {
      ...SanityImage
    }
    desktopImage {
      ...SanityImage
    }
    textBackgroundColor
    withDecorativeArc
  }
`;

interface NumberType {
  superTitle?: string;
  number: number;
  numberUnit?: string;
  subtitle: string;
}

export type TextWithBackgroundImageModuleProps = {
  title?: string;
  text?: RawPortableText;
  numbersTitle?: string;
  numbers?: Array<NumberType>;
  mobileImage: SanityImageType;
  tabletImage?: SanityImageType;
  desktopImage: SanityImageType;
  imagePosition?: 'center' | 'left' | 'right';
  extraImageProps?: Partial<BaseImageProps> & {
    dimensions?: ImageDimensions;
  };
  textBackgroundColor: BackgroundColorTheme;
  withDecorativeArc?: boolean;
  children?: React.ReactNode;
  backgoundChildren?: React.ReactNode;
  withNarrowTextBox?: boolean;
  withBoxShadow?: boolean;
  className?: string;
  contentClassName?: string;
  textContainerWithoutBackground?: boolean;
  withoutImageParallax?: boolean;
};

export function getModuleBgColor(props: TextWithBackgroundImageModuleProps): ModuleBgColor {
  /**
   * The purpose of this function is to let other modules know which background color this module has.
   * Knowing this, we can use this function to make decisions about the layout and spacing between modules.
   */
  return 'colored';
}

function TextWithBackgroundImageModule(
  props: TextWithBackgroundImageModuleProps & CommonModuleProps,
): React.ReactElement {
  const {
    title,
    text,
    numbersTitle,
    numbers,
    textBackgroundColor,
    mobileImage,
    tabletImage,
    desktopImage,
    imagePosition,
    extraImageProps = {},
    withDecorativeArc,
    children,
    backgoundChildren,
    withNarrowTextBox,
    withBoxShadow,
    className,
    contentClassName,
    textContainerWithoutBackground,
    previousModuleBgColor,
    withoutImageParallax,
    moduleId,
  } = props;

  const containerRef = useRef<HTMLElement | null>(null);

  useGSAP(
    () => {
      let mm = gsap.matchMedia();

      gsap.from(`.${styles.title}, .${serializersStyles.listItem}, .${styles.text}`, {
        x: -100,
        opacity: 0,
        duration: 1,
        ease: 'circ.out',
        stagger: { each: 0.2 },

        scrollTrigger: {
          trigger: '.' + styles.textContainer,
          start: 'top 85%',
        } satisfies ScrollTrigger.Vars,
      });

      if (numbers && numbers.length > 0) {
        gsap.from(`.${styles.numberContainer}`, {
          x: 100,
          opacity: 0,
          duration: 1,
          ease: 'circ.out',
          stagger: { each: 0.2 },

          scrollTrigger: {
            trigger: '.' + styles.numbersContainer,
            start: 'top 85%',
          } satisfies ScrollTrigger.Vars,
        });
      }

      mm.add(
        '(min-width:1200px)',

        // image parallax
        () => {
          if (!withoutImageParallax) {
            gsap.fromTo(
              '.' + styles.imageContainer,
              {
                y: '-20%',
                transform: 'scale(1.2)',
                ease: 'none',
              },
              {
                y: '20%',
                scrollTrigger: {
                  start: 'top bottom',
                  end: 'bottom top',
                  trigger: '.' + styles.content,
                  scrub: true,
                } satisfies ScrollTrigger.Vars,
              },
            );
          }
        },
      );
    },
    { scope: containerRef },
  );

  return (
    <ModuleLayout
      id={moduleId}
      moduleRef={containerRef}
      className={clsx(className, styles.container)}
      contentClassName={clsx(styles.contentContainer)}
      childrenClassName={clsx(
        styles.content,
        ((numbers && numbers.length > 0) || !!backgoundChildren) && styles.withBackgroundChildren,
        withDecorativeArc && styles.withDecorativeArc,
        withNarrowTextBox && styles.withNarrowTextBox,
        withBoxShadow && styles.withBoxShadow,
        textContainerWithoutBackground && styles.textContainerWithoutBackground,
        withoutImageParallax && styles.withoutImageParallax,
        contentClassName,
      )}
      childrenWithoutHorizontalPadding
      currentModuleBgColor={getModuleBgColor(props)}
      previousModuleBgColor={previousModuleBgColor}
    >
      <div className={styles.overlay}></div>
      <div className={styles.imageContainer}>
        <Image
          className={clsx(
            styles.image,
            imagePosition === 'left' && styles.leftCentered,
            imagePosition === 'right' && styles.rightCentered,
          )}
          image={mobileImage}
          dimensions={[
            [1000, 1800],
            [BREAKPOINTS.tabletVertical, tabletImage || desktopImage, 960, 700],
            [BREAKPOINTS.smallLaptop, desktopImage, 2880, 1920],
          ]}
          cover
          {...(extraImageProps as any)}
        />
      </div>
      <div
        className={clsx(
          styles.textContainer,
          textBackgroundColor === '#E5F8E8' ? styles.greenBackground : styles.purpleBackground,
          numbers && numbers.length > 0 && styles.lessMarginBottom,
        )}
      >
        {title && <h2 className={clsx(styles.title)}>{title}</h2>}
        {text && <GenericPortableText blocks={text} className={styles.text} />}
        {children}
      </div>
      {numbers && numbers.length > 0 && (
        <div className={styles.numbersContainer}>
          <h3 className={styles.numbersTitle}>{numbersTitle}</h3>
          <div className={styles.numbersGridContainer}>
            {numbers.map((number, i) => (
              <div className={styles.numberContainer} key={i}>
                <div className={styles.numberWrapper}>
                  <span className={styles.number}>{number.number}</span>
                  <div className={styles.superTitleAndUnitContainer}>
                    <span className={styles.superTitle}>{number.superTitle}</span>
                    <span className={styles.unit}>{number.numberUnit}</span>
                  </div>
                </div>
                <p className={styles.subtitle}>{number.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {backgoundChildren && <div className={styles.backgroundChildren}>{backgoundChildren}</div>}
      {withDecorativeArc && <div className={styles.arc}></div>}
    </ModuleLayout>
  );
}

export default TextWithBackgroundImageModule;
